// Auth context for the web app. Emergent-managed Google Sign-In only
// (Apple Sign-In is iOS-only). Keyed by email on the backend, so a user who
// signs in with Google on iOS and on the web is the SAME account -> history syncs.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { authMe, exchangeSession, getToken, setToken, logout as apiLogout, deleteAccount as apiDelete, loginEmail, registerEmail } from "./api";
import { ttTrack, ttIdentify } from "./analytics";

const AuthContext = createContext(null);

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

function extractSessionId(url) {
  const m = url.match(/[?#&]session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading"); // loading | anon | authed
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const processed = useRef(new Set());

  const finishAuth = useCallback((token, u) => {
    setToken(token);
    setUser(u);
    setStatus("authed");
    setError(null);
  }, []);

  const doExchange = useCallback(
    async (sid) => {
      if (processed.current.has(sid)) return;
      processed.current.add(sid);
      try {
        const data = await exchangeSession(sid);
        finishAuth(data.session_token, data.user);
        if (data.is_new) {
          await ttIdentify(data.user?.email);
          ttTrack("CompleteRegistration", "signup", { description: "google_signup" });
        }
      } catch (e) {
        setError(e?.message ?? "Google sign-in failed");
      }
    },
    [finishAuth]
  );

  // Cold-start: handle redirect back from Emergent auth (session_id in URL),
  // otherwise validate a stored token.
  useEffect(() => {
    (async () => {
      const sid = extractSessionId(window.location.href);
      if (sid) {
        await doExchange(sid);
        try {
          const clean = new URL(window.location.href);
          clean.hash = "";
          clean.searchParams.delete("session_id");
          window.history.replaceState(window.history.state, "", clean.toString());
        } catch {}
        return;
      }
      const token = getToken();
      if (!token) {
        setStatus("anon");
        return;
      }
      try {
        const u = await authMe();
        setUser(u);
        setStatus("authed");
      } catch {
        setToken(null);
        setStatus("anon");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.email) ttIdentify(user.email);
  }, [user?.email]);

  const signInWithGoogle = useCallback((next = "/app") => {
    const target = next && next.startsWith("/") ? next : "/app";
    const redirect = `${window.location.origin}${target}`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const signInWithEmail = useCallback(
    async (email, password) => {
      const data = await loginEmail(email, password);
      finishAuth(data.session_token, data.user);
    },
    [finishAuth]
  );

  const registerWithEmail = useCallback(
    async (email, password, name) => {
      const data = await registerEmail(email, password, name);
      finishAuth(data.session_token, data.user);
      if (data.is_new) {
        await ttIdentify(data.user?.email);
        ttTrack("CompleteRegistration", "signup", { description: "email_signup" });
      }
    },
    [finishAuth]
  );

  const signOut = useCallback(async () => {
    await apiLogout();
    setToken(null);
    setUser(null);
    setStatus("anon");
  }, []);

  const deleteAccount = useCallback(async () => {
    await apiDelete();
    setToken(null);
    setUser(null);
    setStatus("anon");
  }, []);

  const value = useMemo(
    () => ({
      status,
      isAuthed: status === "authed",
      user,
      error,
      signInWithGoogle,
      signInWithEmail,
      registerWithEmail,
      signOut,
      deleteAccount,
      clearError: () => setError(null),
    }),
    [status, user, error, signInWithGoogle, signInWithEmail, registerWithEmail, signOut, deleteAccount]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
